import { Box, Text } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import { ComplianceExtraFieldsConfigsFieldFragment } from 'generated/resources/graphql'

import Select from 'modules/core/components/Select'
import i18n from 'modules/core/lib/i18n'

import { DefaultComplianceSettingsFormValues } from './types'

const updateDefaultValue = (
  form: FormikProps<DefaultComplianceSettingsFormValues>,
  fieldName: string,
  value: string | null | undefined,
  formFieldName: 'invoiceDefaultValues' | 'creditNoteDefaultValues'
) => {
  const currentValues = form.values[formFieldName] || []

  if (!value || value === '') {
    const filteredValues = currentValues.filter((item) => item.fieldName !== fieldName)
    form.setFieldValue(formFieldName, filteredValues)
    return
  }

  const existingIndex = currentValues.findIndex((item) => item.fieldName === fieldName)
  const newValue = { fieldName, value }

  if (existingIndex >= 0) {
    const updatedValues = [...currentValues]
    updatedValues[existingIndex] = newValue
    form.setFieldValue(formFieldName, updatedValues)
  } else {
    form.setFieldValue(formFieldName, [...currentValues, newValue])
  }
}

const getCurrentFieldValue = (
  form: FormikProps<DefaultComplianceSettingsFormValues>,
  fieldName: string,
  defaultValue: string | null | undefined,
  formFieldName: 'invoiceDefaultValues' | 'creditNoteDefaultValues'
): string | undefined => {
  if (defaultValue !== null && defaultValue !== undefined) {
    return defaultValue
  }

  const formValues = form.values[formFieldName] || []
  const formValue = formValues.find((item) => item.fieldName === fieldName)
  return formValue?.value
}

interface FieldSectionProps {
  title: string
  fields: ComplianceExtraFieldsConfigsFieldFragment[]
  form: FormikProps<DefaultComplianceSettingsFormValues>
  formFieldName: 'invoiceDefaultValues' | 'creditNoteDefaultValues'
  getDefaultValue: (field: ComplianceExtraFieldsConfigsFieldFragment) => string | null | undefined
  getVisible: (field: ComplianceExtraFieldsConfigsFieldFragment) => boolean
}

export const FieldSection = ({
  title,
  fields,
  form,
  formFieldName,
  getDefaultValue,
  getVisible,
}: FieldSectionProps) => {
  if (!fields || fields.length === 0) return null

  return (
    <Box gap="s16" style={{ flex: '1', minWidth: '0' }}>
      <Text size="400" weight="semibold" color="grey1000">
        {title}
      </Text>
      {fields.map((field) => {
        const hasDefaultValue = !!getDefaultValue(field)
        const isVisible = getVisible(field)
        const currentValue = getCurrentFieldValue(
          form,
          field.name,
          getDefaultValue(field),
          formFieldName
        )

        const isReadonly = hasDefaultValue || !isVisible
        const hasValue = currentValue !== null && currentValue !== undefined && currentValue !== ''

        if (isReadonly && !hasValue) {
          return null
        }

        return (
          <Select
            key={field.name}
            label={field.label}
            value={currentValue}
            onChange={(value) => {
              if (!hasDefaultValue) {
                updateDefaultValue(form, field.name, value, formFieldName)
              }
            }}
            options={
              field.allowedValues?.map((option) => ({
                label: option.label,
                value: option.value,
              })) || []
            }
            readonly={isReadonly}
            missingLabel={i18n.t('common.select')}
            none={i18n.t('common.none')}
          />
        )
      })}
    </Box>
  )
}
